import React, { useEffect, useState, useMemo } from 'react';
import * as Sentry from '@sentry/browser';
import { listAllCompanyOwners } from '../../graphql/resourceLibraryQueries';
import { CustomModal } from '../CustomModal';
import { Btn, IconSearch, TEIQspinner } from '../ui';
import '../../css/ResourceLibraryContent.css';

interface CompanyOwnerItem {
    id: string;
    name: string;
    email: string;
    subscriptionType: string;
    companyId?: string | null;
}

interface AssignmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    resourceName: string;
    resourceType: 'group' | 'plan' | 'doc';
    currentCompanyIds: string[];
    onSave: (companyIds: string[]) => Promise<void>;
    subtitle?: string;
}

export const AssignmentModal: React.FC<AssignmentModalProps> = ({
    isOpen,
    onClose,
    resourceName,
    resourceType,
    currentCompanyIds,
    onSave,
    subtitle,
}) => {
    const [companyOwners, setCompanyOwners] = useState<CompanyOwnerItem[]>([]);
    const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch company owners when modal opens
    useEffect(() => {
        if (!isOpen) return;
        const fetchOwners = async () => {
            setLoading(true);
            try {
                const owners = await listAllCompanyOwners();
                // Filter to only owners with a companyId
                const filtered = (owners as CompanyOwnerItem[]).filter(
                    (o) => o.companyId
                );
                setCompanyOwners(filtered);
            } catch (err) {
                console.error('Error fetching company owners:', err);
                Sentry.captureException(err);
                setError('Failed to load company owners.');
            } finally {
                setLoading(false);
            }
        };
        fetchOwners();
    }, [isOpen]);

    // Pre-check companies matching currentCompanyIds when owners load
    useEffect(() => {
        if (companyOwners.length > 0) {
            const preChecked = new Set<string>();
            for (const owner of companyOwners) {
                if (owner.companyId && currentCompanyIds.includes(owner.companyId)) {
                    preChecked.add(owner.companyId);
                }
            }
            setSelectedCompanyIds(preChecked);
        }
    }, [companyOwners, currentCompanyIds]);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSearchQuery('');
            setError(null);
        }
    }, [isOpen]);

    // Filter companies by search query
    const filteredOwners = useMemo(() => {
        if (!searchQuery.trim()) return companyOwners;
        const q = searchQuery.toLowerCase();
        return companyOwners.filter(
            (o) =>
                o.name.toLowerCase().includes(q) ||
                o.email.toLowerCase().includes(q)
        );
    }, [companyOwners, searchQuery]);

    const handleToggle = (companyId: string) => {
        setSelectedCompanyIds((prev) => {
            const next = new Set(prev);
            if (next.has(companyId)) {
                next.delete(companyId);
            } else {
                next.add(companyId);
            }
            return next;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            await onSave(Array.from(selectedCompanyIds));
            onClose();
        } catch (err) {
            console.error('Error saving assignments:', err);
            Sentry.captureException(err);
            setError('Failed to save assignments. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const resourceTypeLabel =
        resourceType === 'group' ? 'template group' :
            resourceType === 'plan' ? 'plan template' : 'document';

    const assignHeader =
        resourceType === 'group' ? 'Group' : resourceType === 'plan' ? 'Template' : 'Doc';

    return (
        <CustomModal
            visible={isOpen}
            onDismiss={onClose}
            size="large"
            header={`Assign ${assignHeader} — ${resourceName}`}
            bodyClassName="zrl-dialog-body"
            footer={
                <div className="modal-footer-actions">
                    <Btn variant="secondary-outlined" buttonText="Cancel" buttonOnClick={onClose} />
                    <Btn
                        variant="primary"
                        buttonText={saving ? 'Saving...' : 'Save Assignments'}
                        buttonDisabled={saving}
                        buttonOnClick={handleSave}
                    />
                </div>
            }
        >
                    <p className="zrl-modal-subtitle">
                        {subtitle || `Select which company owners can access this ${resourceTypeLabel}`}
                    </p>
                    <div className="zrl-search-bar">
                        <span className="zrl-search-icon"><IconSearch /></span>
                        <input
                            type="text"
                            placeholder="Search companies..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    {loading ? (
                        <div className="zrl-loading">
                            <TEIQspinner label="Loading companies" />
                        </div>
                    ) : filteredOwners.length === 0 ? (
                        <div className="zrl-empty">
                            {searchQuery ? 'No companies match your search.' : 'No companies found.'}
                        </div>
                    ) : (
                        <div className="zrl-company-list">
                            {filteredOwners.map((owner) => {
                                const isChecked = owner.companyId ? selectedCompanyIds.has(owner.companyId) : false;
                                return (
                                    <div
                                        key={owner.id}
                                        className={`zrl-company-row ${isChecked ? 'checked' : ''}`}
                                        onClick={() => owner.companyId && handleToggle(owner.companyId)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => owner.companyId && handleToggle(owner.companyId)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <div className="zrl-company-info">
                                            <div className="zrl-company-name">{owner.name}</div>
                                            <div className="zrl-company-email">{owner.email}</div>
                                        </div>
                                        <span className={`zrl-badge ${owner.subscriptionType === 'Full Access' ? 'zrl-badge-full' : 'zrl-badge-demo'}`}>
                                            {owner.subscriptionType || 'Free Demo'}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Count */}
                    <div className="zrl-assign-count">
                        <strong>{selectedCompanyIds.size}</strong> {selectedCompanyIds.size === 1 ? 'company' : 'companies'} currently assigned to this {resourceTypeLabel}
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="zrl-warning-banner" style={{ marginTop: 12 }}>
                            <span className="zrl-warning-icon">⚠️</span>
                            <span>{error}</span>
                        </div>
                    )}
        </CustomModal>
    );
};
